import { Component, EventEmitter, computed, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import { ContentBinding, DeviceConfigurationSummary, LocationName } from '../models/device-provisioning-request.model';
import { DeviceConfigurationService } from '../../device-configuration/services/device-configuration.service';
import { WizardModal } from '../../../shared/components/wizard-modal/wizard-modal';
import { WizardStep } from '../../../shared/components/wizard-modal/wizard-step';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Combobox, ComboboxOption } from '../../../shared/components/forms/combobox';
import { DeviceProvisioningResponse } from '../models/device-provisioning-response.model';
import { ToastService } from '../../../shared/services/toast-service';
import { FeedService } from '../../../shared/services/feed.service';
import { Feed, FeedResource } from '../../../shared/models/feed.model';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import * as L from 'leaflet';
import { createBasemapLayer, createLocationMarker } from '../../../shared/utils/leaflet-basemap';
import { ENV_CONFIG } from '../../../core/env-config';

export type DialogMode = 'add' | 'edit';

/** One assigned feed row: the raw (tenantId, feedId) pair plus the stops the
 *  display shows from it. `providerName`/`feedName` fall back to the raw ids
 *  when the pair no longer matches any current tenant feed (legacy data).
 *  Always at least one stop. */
interface FeedRow {
  feedId: string;
  providerName: string;
  feedName: string;
  tenantId: string;
  stops: string[];
}

/** Editable row for the location-name step — the wire format is a flat
 *  `{ [lang]: text }` object, edited here as `{lang, text}` rows for a
 *  friendlier add/remove UI, then folded back into an object on submit. */
/** Per-feed stop suggestions: 'loading' while in flight, 'unavailable' when
 *  the feed has no stop list (error — e.g. siri's 502 — or an empty list), in
 *  which case the plain stop-id inputs are all there is. */
type StopSuggestions = FeedResource[] | 'loading' | 'unavailable';

/** Where the device location in the form came from: nothing yet, the admin
 *  (map click / typed coordinates / an existing device location), or a picked
 *  stop's coordinates. Only 'none' and 'stop' may be auto-filled from a stop —
 *  an admin-entered location is never overwritten without them asking. */
type LocationSource = { kind: 'none' } | { kind: 'admin' } | { kind: 'stop'; stopId: string; name: string };

/** A stop's name for display, with its rider-facing code when it has one: "Central (1234)". */
function stopDisplayName(stop: FeedResource): string {
  return stop.code ? `${stop.name} (${stop.code})` : stop.name;
}

/** A stop assigned to the device that has a known position. */
interface StopPosition {
  stopId: string;
  name: string;
  latitude: number;
  longitude: number;
}

interface LocationNameRow {
  lang: string;
  text: string;
}

@Component({
  selector: 'app-add-device-dialog',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslocoDirective,
    LeafletModule,
    WizardModal,
    WizardStep,
    FormField,
    TextInput,
    NumberInput,
    Combobox,
  ],
  templateUrl: './add-provisioned-device.component.html',
  host: {
    // Closes the "Add feed" popup menu on an outside click.
    '(document:mousedown)': 'onDocumentMouseDown($event)',
  },
})
export class AddDeviceDialogComponent implements OnInit, OnDestroy {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroy$ = new Subject<void>();
  private readonly toastService = inject(ToastService);
  private readonly feedService = inject(FeedService);
  private readonly transloco = inject(TranslocoService);
  private readonly env = inject(ENV_CONFIG);
  private readonly deviceConfigurationService = inject(DeviceConfigurationService);

  private leafletMap: L.Map | null = null;
  private marker: L.Marker | null = null;
  /** Keeps the map sized correctly through the modal's entrance animation and
   *  step changes — a single fixed-delay `invalidateSize()` can land mid
   *  animation and skip its redraw, leaving the map blank. */
  private mapResizeObserver: ResizeObserver | null = null;

  @Input() provisionedDeviceInput: DeviceProvisioningResponse | null = null;
  /** True while the parent's save request is in flight; freezes the wizard. */
  @Input() busy = false;
  @Output() deviceAdded = new EventEmitter<{
    clientId: string;
    contentBindings: ContentBinding[];
    latitude: number | null;
    longitude: number | null;
    locationName: LocationName | null;
    deviceConfigurationId: number | null;
    mode: DialogMode;
  }>();
  @Output() dialogClosed = new EventEmitter<void>();

  /** Drives the wizard shell's two-way `open`; closing routes through onClose(). */
  readonly open = signal(true);
  mode: DialogMode = 'add';

  readonly deviceConfigurationOptions = signal<DeviceConfigurationSummary[]>([]);
  readonly assignedFeeds = signal<FeedRow[]>([]);
  readonly isFeedMenuOpen = signal(false);

  readonly basicsForm = this.fb.group({
    clientId: [''],
  });
  locationNames: LocationNameRow[] = [{ lang: 'en', text: '' }];

  readonly locationForm = this.fb.group({
    latitude: [0, [Validators.min(-90), Validators.max(90)]],
    longitude: [0, [Validators.min(-180), Validators.max(180)]],
  });

  readonly configForm = this.fb.group({
    deviceConfigurationId: this.fb.control<number | null>(null),
  });

  readonly tileLayer = createBasemapLayer(this.env.cartoApiKey);

  mapOptions: L.MapOptions = {
    layers: [this.tileLayer],
    zoom: 4,
    center: L.latLng(51.505, -0.09),
  };
  mapLayers: L.Layer[] = [];

  /** All tenant feeds, flattened with their provider name for lookup + labelling. */
  private flatFeeds: { providerName: string; feed: Feed }[] = [];

  ngOnInit(): void {
    this.mode = this.provisionedDeviceInput !== null ? 'edit' : 'add';
    if (this.mode === 'add') {
      this.basicsForm.controls.clientId.addValidators(Validators.required);
    }

    if (this.provisionedDeviceInput !== null) {
      const device = this.provisionedDeviceInput;
      this.basicsForm.patchValue({ clientId: device.clientId });
      this.configForm.patchValue({ deviceConfigurationId: device.deviceConfigurationId });

      const nameEntries = Object.entries(device.locationName ?? {});
      this.locationNames = nameEntries.length ? nameEntries.map(([lang, text]) => ({ lang, text })) : [{ lang: 'en', text: '' }];

      const latitude = device.latitude ?? 0;
      const longitude = device.longitude ?? 0;
      this.locationForm.patchValue({ latitude, longitude });
      if (latitude !== 0 || longitude !== 0) {
        // An existing device location is the admin's — never auto-replaced by a stop.
        this.locationSource.set({ kind: 'admin' });
        this.mapOptions = { ...this.mapOptions, zoom: 14, center: L.latLng(latitude, longitude) };
        this.marker = createLocationMarker([latitude, longitude]);
        this.mapLayers = [this.marker];
      }
    }

    this.deviceConfigurationService.getListForDeviceConfigurationAssignmentUsage().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => { this.deviceConfigurationOptions.set(data); },
      error: (error) => {
        console.error('Error getting device configuration options:', error);
        this.toastService.show(this.transloco.translate('devices.wizard.toast.loadConfigsFailed'), 'error');
      }
    });

    this.feedService.getAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.flatFeeds = Object.entries(res.data).flatMap(([providerName, feeds]) =>
          feeds.map((feed) => ({ providerName, feed })),
        );
        this.reconcileAssignedFeeds();
      },
      error: (error) => {
        console.error('Error getting feeds:', error);
        this.toastService.show(this.transloco.translate('devices.wizard.toast.loadFeedsFailed'), 'error');
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.mapResizeObserver?.disconnect();
  }

  // ── Feeds & stops (step 2) ────────────────────────────────────────────

  /** Stop suggestions keyed by feedId, fetched once per feed for the form's
   *  lifetime. Suggestions only help fill the inputs — `row.stops` stays the
   *  source of truth, and free-typed ids are always accepted. */
  readonly stopSuggestions = signal<Record<string, StopSuggestions>>({});

  /** Combobox rows per feed: the stop name, with "code · stopId" (or just the
   *  stopId) as the hint — all searchable. A code isn't unique, so searching it
   *  lists every stop sharing it; the name and stopId tell them apart. The value
   *  is always the stopId. */
  readonly stopComboOptions = computed<Record<string, ComboboxOption[]>>(() => {
    const out: Record<string, ComboboxOption[]> = {};
    for (const [feedId, s] of Object.entries(this.stopSuggestions())) {
      if (Array.isArray(s)) out[feedId] = s.map((stop) => ({ value: stop.resourceId, label: stop.name, hint: stop.code ? `${stop.code} · ${stop.resourceId}` : stop.resourceId }));
    }
    return out;
  });

  stopOptions(feedId: string): FeedResource[] {
    const s = this.stopSuggestions()[feedId];
    return Array.isArray(s) ? s : [];
  }

  isStopListUnavailable(feedId: string): boolean {
    return this.stopSuggestions()[feedId] === 'unavailable';
  }

  /** The picked stop's name (with its code, when it has one), for showing next to a typed/picked stop id. */
  stopName(feedId: string, stopId: string): string | null {
    if (!stopId) return null;
    const stop = this.stopOptions(feedId).find((s) => s.resourceId === stopId);
    return stop ? stopDisplayName(stop) : null;
  }

  private loadStops(feedId: string): void {
    if (this.stopSuggestions()[feedId]) return;
    this.stopSuggestions.update((m) => ({ ...m, [feedId]: 'loading' }));
    this.feedService.getResources(feedId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const stops = res.data ?? [];
        this.stopSuggestions.update((m) => ({ ...m, [feedId]: stops.length ? stops : 'unavailable' }));
      },
      // Silent: no stop list just means manual entry (siri returns 502 today).
      error: () => this.stopSuggestions.update((m) => ({ ...m, [feedId]: 'unavailable' })),
    });
  }


  /** In edit mode, group the device's existing (tenantId, feedId, resourceId)
   *  content bindings by (tenantId, feedId) pair once feeds have loaded, matching
   *  each group back to a feed — legacy rows that no longer match any
   *  current feed still render (with raw ids as the label), so editing
   *  doesn't lose data. */
  private reconcileAssignedFeeds(): void {
    const bindings = this.provisionedDeviceInput?.contentBindings ?? [];
    if (!bindings.length) return;

    const rows = new Map<string, FeedRow>();
    const order: string[] = [];
    for (const { tenantId, feedId, resourceId } of bindings) {
      const key = `${tenantId}||${feedId}`;
      if (!rows.has(key)) {
        const match = this.flatFeeds.find((f) => f.feed.tenantId === tenantId && f.feed.id === feedId);
        rows.set(key, {
          feedId,
          providerName: match?.providerName ?? '',
          feedName: match?.feed.name ?? `${tenantId} / ${feedId}`,
          tenantId,
          stops: [],
        });
        order.push(key);
      }
      rows.get(key)!.stops.push(resourceId);
    }
    if (!order.length) return;
    this.assignedFeeds.set(order.map((key) => rows.get(key)!));
    for (const key of order) this.loadStops(rows.get(key)!.feedId);
  }

  /** Feeds not already assigned to this device, for the "Add feed" menu. */
  get unassignedFeedOptions(): { providerName: string; feed: Feed }[] {
    const assignedIds = new Set(this.assignedFeeds().map((r) => r.feedId));
    return this.flatFeeds.filter((f) => !assignedIds.has(f.feed.id));
  }

  toggleFeedMenu(): void {
    this.isFeedMenuOpen.update((v) => !v);
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isFeedMenuOpen()) return;
    const target = event.target as HTMLElement;
    if (!target.closest('[data-feed-menu]')) this.isFeedMenuOpen.set(false);
  }

  addFeed(feed: Feed, providerName: string): void {
    this.assignedFeeds.update((rows) => [
      ...rows,
      { feedId: feed.id, providerName, feedName: feed.name, tenantId: feed.tenantId, stops: [''] },
    ]);
    this.isFeedMenuOpen.set(false);
    this.loadStops(feed.id);
  }

  removeFeed(index: number): void {
    this.assignedFeeds.update((rows) => rows.filter((_, i) => i !== index));
  }

  addStop(rowIndex: number): void {
    this.assignedFeeds.update((rows) =>
      rows.map((row, i) => (i === rowIndex ? { ...row, stops: [...row.stops, ''] } : row)),
    );
  }

  removeStop(rowIndex: number, stopIndex: number): void {
    this.assignedFeeds.update((rows) =>
      rows.map((row, i) => (i === rowIndex ? { ...row, stops: row.stops.filter((_, si) => si !== stopIndex) } : row)),
    );
  }

  setStop(rowIndex: number, stopIndex: number, value: string): void {
    this.assignedFeeds.update((rows) =>
      rows.map((row, i) =>
        i === rowIndex ? { ...row, stops: row.stops.map((s, si) => (si === stopIndex ? value : s)) } : row,
      ),
    );
    // A picked (or exactly typed) stop with a position pre-fills the device
    // location — unless the admin already set one themselves.
    const row = this.assignedFeeds()[rowIndex];
    const stop = row ? this.stopOptions(row.feedId).find((s) => s.resourceId === value) : undefined;
    const kind = this.locationSource().kind;
    if (stop?.coordinates && (kind === 'none' || kind === 'stop')) {
      this.applyStopPosition({ stopId: stop.resourceId, name: stopDisplayName(stop), ...stop.coordinates });
    }
  }

  // ── Stop positions → device location ──────────────────────────────────

  readonly locationSource = signal<LocationSource>({ kind: 'none' });

  /** Assigned stops (deduplicated) whose feed reported a position — offered on
   *  the Location step as "use this stop's position". */
  readonly stopPositions = computed<StopPosition[]>(() => {
    const out = new Map<string, StopPosition>();
    for (const row of this.assignedFeeds()) {
      for (const stopId of row.stops) {
        const stop = this.stopOptions(row.feedId).find((s) => s.resourceId === stopId);
        if (stop?.coordinates && !out.has(stop.resourceId)) {
          out.set(stop.resourceId, { stopId: stop.resourceId, name: stopDisplayName(stop), ...stop.coordinates });
        }
      }
    }
    return [...out.values()];
  });

  /** Whether the device location currently is this stop's position (set from it). */
  isAppliedStopPosition(position: StopPosition): boolean {
    const source = this.locationSource();
    return source.kind === 'stop' && source.stopId === position.stopId;
  }

  /** Use a stop's position as the device location (explicit choice on the
   *  Location step, or the auto-fill above). */
  applyStopPosition(position: StopPosition): void {
    const latitude = parseFloat(position.latitude.toFixed(6));
    const longitude = parseFloat(position.longitude.toFixed(6));
    this.locationForm.setValue({ latitude, longitude });
    this.placeMarker(latitude, longitude);
    // The map may not exist (or may be rebuilt) when the Location step renders —
    // centre its initial options too, and move a live map directly.
    this.mapOptions = { ...this.mapOptions, zoom: 16, center: L.latLng(latitude, longitude) };
    if (this.leafletMap?.getContainer().isConnected) this.leafletMap.setView([latitude, longitude], 16);
    this.locationSource.set({ kind: 'stop', stopId: position.stopId, name: position.name });
  }

  get totalStopCount(): number {
    return this.assignedFeeds().reduce((n, row) => n + row.stops.length, 0);
  }

  // ── Location names (step 1) ───────────────────────────────────────────

  addLocationName(): void {
    this.locationNames = [...this.locationNames, { lang: 'en', text: '' }];
  }

  removeLocationName(index: number): void {
    this.locationNames = this.locationNames.filter((_, i) => i !== index);
    if (this.locationNames.length === 0) this.locationNames = [{ lang: 'en', text: '' }];
  }

  trackByIndex(index: number, _item: unknown): number { return index; }

  // ── Map (step 3) ───────────────────────────────────────────────────────

  onMapReady(map: L.Map): void {
    this.leafletMap = map;
    setTimeout(() => {
      map.invalidateSize();
      map.setView(map.getCenter(), map.getZoom());
    });
    this.mapResizeObserver?.disconnect();
    this.mapResizeObserver = new ResizeObserver(() => map.invalidateSize());
    this.mapResizeObserver.observe(map.getContainer());
  }

  onMapClick(e: L.LeafletMouseEvent): void {
    const { lat, lng } = e.latlng;
    const latitude = parseFloat(lat.toFixed(6));
    const longitude = parseFloat(lng.toFixed(6));
    this.locationForm.setValue({ latitude, longitude });
    this.placeMarker(latitude, longitude);
    this.locationSource.set({ kind: 'admin' });
  }

  /** Fires for typed coordinates only (not programmatic setValue). */
  onCoordsInput(): void {
    this.locationSource.set({ kind: 'admin' });
    const { latitude, longitude } = this.locationForm.getRawValue();
    if (!this.isValidLatitude(latitude) || !this.isValidLongitude(longitude)) return;
    this.placeMarker(latitude, longitude);
    this.leafletMap?.panTo([latitude, longitude]);
  }

  private placeMarker(lat: number, lng: number): void {
    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      this.marker = createLocationMarker([lat, lng]);
      this.mapLayers = [this.marker];
    }
  }

  isValidLatitude(value: number): boolean { return value >= -90 && value <= 90; }
  isValidLongitude(value: number): boolean { return value >= -180 && value <= 180; }

  // ── Review / submit ────────────────────────────────────────────────────

  selectedConfiguration(): DeviceConfigurationSummary | null {
    const id = this.configForm.getRawValue().deviceConfigurationId;
    return this.deviceConfigurationOptions().find((c) => c.id === id) ?? null;
  }

  /** "1280×720 · Europe/London · 2 ports" — built only from whatever the
   *  backend actually sends (displayWidth/Height/timezone/displayPortCount
   *  are optional on the lean list DTO today; see the model's doc comment).
   *  Null when none of those fields are present, so the card falls back to
   *  a name-only row instead of showing an empty second line. */
  configurationSpecLine(cfg: DeviceConfigurationSummary): string | null {
    const parts: string[] = [];
    if (cfg.displayWidth != null && cfg.displayHeight != null) {
      parts.push(`${cfg.displayWidth}×${cfg.displayHeight}`);
    }
    if (cfg.timezone) parts.push(cfg.timezone);
    if (cfg.displayPortCount != null) {
      parts.push(
        this.transloco.translate('devices.wizard.fields.portCount', { count: cfg.displayPortCount }),
      );
    }
    return parts.length ? parts.join(' · ') : null;
  }

  /** One binding per picked stop; the stop id becomes the binding's resourceId. */
  private buildContentBindings(): ContentBinding[] {
    const bindings: ContentBinding[] = [];
    for (const row of this.assignedFeeds()) {
      for (const resourceId of row.stops) {
        bindings.push({ tenantId: row.tenantId, feedId: row.feedId, resourceId });
      }
    }
    return bindings;
  }

  /** Folds the editable `{lang, text}` rows back into the wire's flat
   *  `{ [lang]: text }` object, dropping rows with no language or no text.
   *  Returns null when nothing is left, so the payload sends `null` rather
   *  than `{}`. */
  private buildLocationName(): LocationName | null {
    const entries = this.locationNames
      .filter((row) => row.lang.trim() && row.text.trim())
      .map((row) => [row.lang.trim(), row.text.trim()] as const);
    return entries.length ? Object.fromEntries(entries) : null;
  }

  onFinished(): void {
    const { latitude, longitude } = this.locationForm.getRawValue();
    this.deviceAdded.emit({
      clientId: this.basicsForm.getRawValue().clientId,
      contentBindings: this.buildContentBindings(),
      latitude,
      longitude,
      locationName: this.buildLocationName(),
      deviceConfigurationId: this.configForm.getRawValue().deviceConfigurationId,
      mode: this.mode,
    });
  }

  onClose(): void {
    this.dialogClosed.emit();
  }
}
